import { startOfToday } from 'date-fns';

import { DateDomainService } from '@/domain/services';
import { brand, memo, updatedAt } from '@/pages/menu-managements/const';
import { Title } from '@/pages/menu-managements/services/title';

const { formatDay } = DateDomainService;

const titleJa = {
  key: 'titleJa',
  label: 'メニュー提供時間名',
};

export const dayOfWeekPeriods = {
  key: 'day_of_week_periods',
  label: 'メニュー提供時間',
};

export const menuForm = {
  brand,
  titleJa,
  memo,
} as const;

const tableColumns = [
  {
    ...titleJa,
    width: '250px',
  },
  {
    ...dayOfWeekPeriods,
    width: '250px',
  },
  {
    ...memo,
    width: '250px',
  },
  {
    ...updatedAt,
    width: '180px',
  },
] as const;

export const businessHourTableThs = tableColumns.map((tableColumn) => {
  return {
    label: tableColumn.label,
    minWidth: tableColumn.width,
  };
});

export type MenuManagementBrandMenuHour = {
  id: number;
  title: Title;
  day_of_week_periods: DayOfWeekPeriodString[];
  memo: string | null;
  updated_at: Date;
};

export type HandleChangeTime = (
  time: string,
  dayOfWeekIndex: number,
  periodIndex: number
) => void;

export const DAY_OF_WEEK = [
  'MONDAY',
  'TUESDAY',
  'WEDNESDAY',
  'THURSDAY',
  'FRIDAY',
  'SATURDAY',
  'SUNDAY',
] as const;

export const dayOfWeekToJa = {
  MONDAY: '月曜日',
  TUESDAY: '火曜日',
  WEDNESDAY: '水曜日',
  THURSDAY: '木曜日',
  FRIDAY: '金曜日',
  SATURDAY: '土曜日',
  SUNDAY: '日曜日',
};

export type DayOfWeek = typeof DAY_OF_WEEK[number];

export type PeriodDate = {
  start_time: Date;
  end_time: Date;
};

export type PeriodString = Record<keyof PeriodDate, string>;

export type DayOfWeekPeriod = {
  day_of_week: DayOfWeek;
  periods: PeriodDate[];
};

export type DayOfWeekPeriodString = {
  day_of_week: DayOfWeek;
  periods: PeriodString[];
};

export const convertToTimeFormat = (time: string): string => {
  const hours = time.slice(0, 2);
  const minutes = time.slice(2);
  return `${hours}:${minutes}`;
};

export const menuHourPeriodTimes = (
  dayOfWeekPeriods: DayOfWeekPeriodString[]
): string => {
  return dayOfWeekPeriods
    .map((dowp) => {
      const periodTimes = dowp.periods
        .map((period) => {
          const formatStartTime = convertToTimeFormat(period.start_time);
          const formatEndTime = convertToTimeFormat(period.end_time);
          return `${formatStartTime}~${formatEndTime}`;
        })
        .join(', ');
      return `${dayOfWeekToJa[dowp.day_of_week]}: ${periodTimes}`;
    })
    .join('、');
};

const getTime = (startTime: string, endTime: string) => {
  const formatStr = formatDay(startOfToday());
  const formatStartTime = convertToTimeFormat(startTime);
  const formatEndTime = convertToTimeFormat(endTime);
  const newStartTime = new Date(`${formatStr} ${formatStartTime}`);
  const newEndTime = new Date(`${formatStr} ${formatEndTime}`);
  return { newStartTime, newEndTime };
};

export const buildBrandMenus = (
  dayOfWeekPeriods: DayOfWeekPeriodString[]
): DayOfWeekPeriod[] => {
  const newDayOfWeekPeriods: DayOfWeekPeriod[] = DAY_OF_WEEK.map(
    (dayOfWeek) => {
      const targetdayOfWeekPeriod = dayOfWeekPeriods.find(
        (dowp) => dowp.day_of_week === dayOfWeek
      );
      return {
        day_of_week: dayOfWeek,
        periods:
          targetdayOfWeekPeriod !== undefined
            ? targetdayOfWeekPeriod.periods.map((period) => {
                const { newStartTime, newEndTime } = getTime(
                  period.start_time,
                  period.end_time
                );
                return {
                  start_time: newStartTime,
                  end_time: newEndTime,
                };
              })
            : [],
      };
    }
  );
  return newDayOfWeekPeriods;
};
